<template>
  <table class="time-sheet-details-body-table">
    <thead>
      <tr>
        <th>Date</th>
        <th v-for="rate in sortedRates" :key="rate.id">{{ rate.rateCode }}</th>
        <th>Total</th>
        <th>Notes</th>
      </tr>
    </thead>

    <tbody>
      <tr v-for="(entriesForDay, date) in entriesByDay" :key="date">
        <td class="date">{{ date | formatDateIgnoreTime('ddd DD/MM') }}</td>
        <td v-for="entry in entriesForDay" :key="entry.id">
          <TimeSheetEntry
            :id="entry.rateCode + '-' + date"
            :value="entry"
            :can-edit="canEdit"
            @change="onEntryChange(entry, $event)"
          />
        </td>
        <td class="hours">
          {{ dateTotalsHourly[date] }} hours
          <br />
          <small>{{ dateTotalsDaily[date] }} dailies</small>
        </td>
        <td>
          <textarea
            v-if="canEdit && notes[date]"
            v-model="notes[date].note"
            rows="1"
            @change="onChange"
          />
          <span v-else-if="notes[date]">{{ notes[date].note }}</span>
        </td>
      </tr>
    </tbody>
    <tfoot>
      <tr>
        <th>Totals</th>

        <td v-for="(total, rate) in rateTotals" :key="rate">{{ total }}</td>

        <th>
          {{ grandTotalHours }} hours
          <br />
          <small>{{ grandTotalDays }} dailies</small>
        </th>

        <th>
          <textarea
            v-if="canEdit && notes['null']"
            v-model="notes['null'].note"
            rows="3"
            @change="onChange"
          />
          <span v-else-if="notes['null']">{{ notes['null'].note }}</span>
        </th>
      </tr>
    </tfoot>

    <tr class="pdf-footer">
      <th>Totals</th>

      <td v-for="(total, rate) in rateTotals" :key="rate">{{ total }}</td>
      <th>
        {{ grandTotalHours }} hours
        <br />
        <small>{{ grandTotalDays }} dailies</small>
      </th>
      <th>
        <textarea
          v-if="canEdit && notes['null']"
          v-model="notes['null'].note"
          rows="3"
          @change="onChange"
        />
        <span v-else-if="notes['null']">{{ notes['null'].note }}</span>
      </th>
    </tr>
  </table>
</template>

<script>
import * as R from 'ramda';
import TimeSheetEntry from './TimeSheetEntry';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';
import i18n from '@/i18n';
export const hourlyFilter = R.filter(n => n.unit === 'HOURLY');
export const dailyFilter = R.filter(n => n.unit === 'DAILY');

export const groupByDay = R.groupBy(entry => entry.entryDate);
export const groupByRate = R.groupBy(entry => `${entry.sortOrder}_${entry.timeSheetRateId}`);

// make an object with key=date and value=note object
export const createNotesObject = timeSheetNotes => {
  if (timeSheetNotes === undefined || timeSheetNotes === null) {
    return {};
  }
  return R.zipObj(R.map(R.prop('noteDate'), timeSheetNotes), R.clone(timeSheetNotes));
};

// notesObject is an object with many pairs of (key=noteDate, value=note object),
// we need to turn it into an array of notes
export const createNotesArray = notesObject => {
  const notesArray = [];
  for (const noteDate in notesObject) {
    notesArray.push(notesObject[noteDate]);
  }
  return notesArray;
};

export const sumFilteredQuantities = (filter, entries) => {
  return R.map(entry => {
    return R.sum(
      R.pipe(
        filter,
        R.pluck('quantity')
      )(entry)
    );
  })(entries);
};

export const sumQuantities = R.map(entry => {
  return R.sum(R.pluck('quantity')(entry));
});

export default {
  name: 'TimeSheetDetailsTable',
  components: {
    TimeSheetEntry
  },
  props: {
    timeSheet: {
      type: Object,
      required: true
    },
    canEdit: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      entries: [],
      entriesByDay: {},
      entriesByRate: {},
      notes: {}
    };
  },
  computed: {
    sortedRates() {
      // We always want to display the columns in the order: regular, overtime, pager shifts
      let val;
      if (this.timeSheet && this.timeSheet.timeSheetRates) {
        val = R.sortBy(R.prop('sortOrder'))(this.timeSheet.timeSheetRates);
        return val;
      }
      return [];
    },
    dateTotalsHourly() {
      return sumFilteredQuantities(hourlyFilter, this.entriesByDay);
    },
    dateTotalsDaily() {
      return sumFilteredQuantities(dailyFilter, this.entriesByDay);
    },
    rateTotals() {
      return sumQuantities(this.entriesByRate);
    },
    grandTotalHours() {
      return R.sum(R.values(this.dateTotalsHourly));
    },
    grandTotalDays() {
      return R.sum(R.values(this.dateTotalsDaily));
    }
  },
  watch: {
    timeSheet: {
      immediate: true,
      handler() {
        this.entries = R.clone(this.timeSheet.timeSheetEntries);
        if (this.entries !== undefined) {
          this.entriesByDay = groupByDay(this.entries);
          for (const item of Object.keys(this.entriesByDay)) {
            this.entriesByDay[item] = this.sortByRateCode(this.entriesByDay[item]);
          }
          this.entriesByRate = groupByRate(this.entries);
          this.notes = createNotesObject(this.timeSheet.timeSheetNotes);
        }
      }
    }
  },
  methods: {
    onEntryChange(entry, $event) {
      let entryCE = this.entriesByDay[`${entry.entryDate}`].find(el => el.rateCode === 'CE');
      if (isNaN($event.quantity)) {
        this.$store.dispatch(TolErrorsActions.SetError, {
          message: i18n.t('timeSheets.details.errors.hoursNotNumeric')
        });
        $event.quantity = 0;
      } else if ($event.quantity < 0 || $event.quantity > 24) {
        this.$store.dispatch(TolErrorsActions.SetError, {
          message: i18n.t('timeSheets.details.errors.hoursGreatherThanTwentyFourOrLessThanZero')
        });
        $event.quantity = 0;
      } else {
        if (entryCE) {
          entryCE.quantity = $event.quantity;
        }
        entry.quantity = $event.quantity;
      }
      this.onChange();
    },
    onChange() {
      const notesArray = createNotesArray(this.notes);
      this.$emit('change', { entries: this.entries, notes: notesArray });
    },
    sortByRateCode(entry) {
      return entry.sort((a, b) => {
        return a.sortOrder - b.sortOrder;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../../styles/colors.scss';
@import '../../../../../styles/variables.scss';

.time-sheet-details-body-table {
  border-collapse: collapse;

  textarea {
    width: 100%;
    font-size: inherit;
  }

  .editable {
    display: inline;
  }

  .readonly {
    display: none;
  }

  td,
  th {
    padding: 5px;
  }

  thead {
    background-color: $background-level-3;
    font-size: 18px;
    font-weight: bold;
    letter-spacing: -0.18px;
    line-height: 21px;
    page-break-inside: avoid;
    tr {
      border-top: 20px solid $background-level-3;
      border-bottom: 20px solid $background-level-3;
    }
  }
  tbody {
    background-color: $background-level-2;
    border-top: 10px;
    font-size: 16px;
    tr td {
      padding-top: 20px;
      padding-bottom: 20px;
      border-bottom: 1pt solid $row-border;
    }
  }
  tr th:first-child,
  tr td:first-child {
    padding-left: $details-padding;
  }
  tr th:last-child,
  tr td:last-child {
    padding-right: $details-padding;
  }
  tfoot {
    background-color: $background-level-3;
  }
  td.date {
    font-weight: bold;
    text-align: left;
  }

  .pdf-footer {
    display: none;
  }
}

@media print {
  .time-sheet-details-body-table {
    thead {
      tr {
        font-size: 12px;
        border-top: 2px solid $black;
        border-bottom: 2px solid $black;
        page-break-inside: avoid;
        th {
          white-space: nowrap;
        }
      }
    }
    span.readonly {
      display: inline;
    }

    textarea.editable {
      display: none;
    }

    tbody {
      background-color: $white;
      font-size: 11px;
      tr td {
        padding: 0;
        &.hours {
          white-space: nowrap;
        }
      }
    }

    tr {
      page-break-inside: avoid;
    }

    tr th:first-child,
    tr td:first-child {
      padding-left: 0;
    }
    tr th:last-child,
    tr td:last-child {
      padding-right: 0;
    }

    textarea {
      border: none;
      outline: none;
      resize: none;
      overflow-wrap: normal;
    }

    tfoot {
      display: none;
    }

    .pdf-footer {
      display: table-row;
      background-color: $light-grey-2;
      border-top: 2px solid $black;
      border-bottom: 2px solid $black;
      page-break-inside: avoid;
      padding: 5px 0;
      font-size: 11px;
      textarea {
        background-color: $light-grey-2;
      }

      th {
        white-space: nowrap;
        padding: 2px 0;
      }
    }
  }
}

@media only screen and (max-width: 768px) {
  .time-sheet-details-body-table {
    * {
      font-size: 0.8rem;
    }
    thead {
      tr {
        border-top: 10px solid $background-level-3;
        border-bottom: 10px solid $background-level-3;
      }
    }
    tbody {
      tr td {
        padding-top: 5px;
        padding-bottom: 5px;
      }
    }

    tr th:first-child,
    tr td:first-child {
      padding-left: $responsive-details-padding;
    }
    tr th:last-child,
    tr td:last-child {
      padding-right: $responsive-details-padding;
    }
  }
}
</style>
